// RoomDetails.tsx - Fixed version
import {
  AdultsDropdown,
  CheckIn,
  CheckOut,
  KidsDropdown,
  ScrollToTop,
} from "../components";
import { useRoomStore } from "../store/roomStore";
import { hotelRules } from "../data";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaArrowLeft,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaBed,
  FaUsers,
  FaStar,
  FaCalendarAlt,
} from "react-icons/fa";
import { useState, useEffect, useCallback } from "react";
import type { Facility } from "../types";
import RoomAvailabilityCalendar from "../components/RoomAvailabilityCalendar";

// Sample JSON data structure for room images
const ROOM_IMAGES_DATA = {
  "1": [
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1200",
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1200",
    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=1200",
    "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=1200",
  ],
  "2": [
    "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=1200",
    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=1200",
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1200",
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1200",
  ],
  "3": [
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1200",
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1200",
    "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=1200",
    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=1200",
  ],
};

interface RoomDetailsProps {
  roomId: number;
  onBack: () => void;
}

export default function RoomDetails({
  roomId,
  onBack,
}: RoomDetailsProps) {
  const { rooms } = useRoomStore();
  const room = rooms.find((r) => r.id === roomId);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [roomImages, setRoomImages] = useState<string[]>([]);
  const [selectedCheckIn, setSelectedCheckIn] = useState<Date | null>(null);
  const [selectedCheckOut, setSelectedCheckOut] = useState<Date | null>(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalNights, setTotalNights] = useState(0);

  // Handle price update from calendar
  const handlePriceUpdate = useCallback((total: number) => {
    console.log("Price update received:", total);
    setTotalPrice(total);
  }, []);

  // Handle date selection
  const handleCalendarDateSelect = useCallback((date: Date | null) => {
    setSelectedCheckIn(date);
    console.log("Selected date:", date);
  }, []);

  // Handle date range selection
  const handleDateRangeSelect = useCallback((checkIn: Date | null, checkOut: Date | null) => {
    setSelectedCheckIn(checkIn);
    setSelectedCheckOut(checkOut);
    if (checkIn && checkOut) {
      const nights = Math.ceil(
        (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
      );
      setTotalNights(nights);
      console.log("Date range selected:", checkIn, checkOut, "Nights:", nights);
    } else {
      setTotalNights(0);
    }
  }, []);

  // Handle book now
  const handleBookNow = useCallback(() => {
    if (selectedCheckIn && selectedCheckOut) {
      console.log("Booking room:", room?.id, "Total:", totalPrice);
      // Navigate to checkout or show booking modal
      alert(`Booking confirmed!\nRoom: ${room?.name}\nCheck-in: ${selectedCheckIn.toLocaleDateString()}\nCheck-out: ${selectedCheckOut.toLocaleDateString()}\nTotal: $${totalPrice}`);
    } else {
      alert("Please select check-in and check-out dates first.");
    }
  }, [selectedCheckIn, selectedCheckOut, totalPrice, room]);

  // Fetch room images based on room ID
  useEffect(() => {
    if (room) {
      const images = ROOM_IMAGES_DATA[room.id as keyof typeof ROOM_IMAGES_DATA] || [
        room.imageLg,
        room.imageLg,
        room.imageLg,
        room.imageLg,
      ];
      setRoomImages(images);
    }
  }, [room]);

  const handleGoBack = () => {
    onBack();
  };

  const handleImageClick = (index: number) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
  };

  const handlePrevImage = () => {
    setSelectedImage((prev) => (prev === 0 ? roomImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImage((prev) => (prev === roomImages.length - 1 ? 0 : prev + 1));
  };

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      if (e.key === "Escape") handleLightboxClose();
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isLightboxOpen]);

  if (!room) {
    return (
      <section className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <ScrollToTop />
        <div className="container mx-auto max-w-7xl px-4 py-24 text-center">
          <div className="bg-white shadow-2xl rounded-2xl p-12 max-w-md mx-auto border border-gray-100">
            <div className="text-6xl mb-6">🔍</div>
            <h2 className="text-3xl font-primary font-bold text-gray-800 mb-4">
              Room Not Found
            </h2>
            <p className="text-gray-500 font-secondary leading-relaxed">
              The room you're looking for doesn't exist or is currently
              unavailable.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Please check your selection or browse our other rooms.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-primary font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              <FaArrowLeft className="text-sm" />
              Go Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  const { name, shortDescription, description, facilities, price, imageLg } = room;
  const displayImages = roomImages.length > 0 ? roomImages : [imageLg];

  return (
    <section className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <ScrollToTop />

      {/* Hero Section */}
      <div
        className="relative h-[500px] lg:h-[600px] flex justify-center items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${imageLg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/70" />

        {/* Back Button */}
        <button
          onClick={handleGoBack}
          className="absolute top-6 left-4 md:top-8 md:left-8 z-30 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2.5 rounded-lg transition-all duration-200 hover:scale-105"
        >
          <FaArrowLeft className="text-sm" />
          <span className="font-secondary text-sm font-medium hidden sm:inline">
            Back
          </span>
        </button>

        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-primary font-bold mb-4">
            {name}
          </h1>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-white/90 font-secondary text-lg max-w-2xl mx-auto">
            {shortDescription ?? "Experience luxury and comfort in our premium room"}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-4">
        <div className="flex flex-col lg:flex-row lg:gap-x-12 py-12 lg:py-16">
          {/* Room Info Section */}
          <div className="w-full lg:flex-1">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <h2 className="font-primary text-3xl lg:text-4xl font-bold text-gray-800">
                {name}
              </h2>
              <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                ${price}/night
              </span>
              <div className="flex items-center gap-1 text-yellow-400">
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </div>
            </div>

            <p className="text-gray-600 font-secondary leading-relaxed mb-8 text-lg">
              {description}
            </p>

            {/* Modern Multi-Image Gallery */}
            {displayImages.length > 0 && (
              <div className="mb-8">
                <div className="relative rounded-xl overflow-hidden shadow-xl cursor-pointer group">
                  <img
                    className="w-full h-[400px] object-cover"
                    src={displayImages[selectedImage] || imageLg}
                    alt={`${name} - Image ${selectedImage + 1}`}
                    loading="lazy"
                    onClick={() => handleImageClick(selectedImage)}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm">
                      Click to expand
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                    {selectedImage + 1} / {displayImages.length}
                  </div>
                </div>

                {/* Thumbnail Grid */}
                {displayImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    {displayImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`relative rounded-lg overflow-hidden transition-all duration-200 ${
                          selectedImage === index
                            ? "ring-2 ring-blue-600 ring-offset-2 scale-95"
                            : "hover:scale-105 hover:shadow-lg"
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${name} thumbnail ${index + 1}`}
                          className="w-full h-20 object-cover"
                          loading="lazy"
                        />
                        {selectedImage === index && (
                          <div className="absolute inset-0 bg-blue-600/10" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-12">
              <h3 className="font-primary text-2xl font-bold text-gray-800 mb-4">
                Amenities & Facilities
              </h3>
              <div className="w-16 h-1 bg-blue-600 mb-6"></div>
              <p className="text-gray-600 font-secondary leading-relaxed mb-8">
                Our rooms come equipped with modern amenities to ensure your
                stay is comfortable and enjoyable.
              </p>

              {/* Facilities Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
                {facilities.map((item: Facility, index: number) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-x-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md border border-gray-100 transition-all duration-200 hover:scale-105 hover:border-blue-200"
                    >
                      <div className="text-2xl text-blue-600 flex-shrink-0">
                        <Icon />
                      </div>
                      <div className="font-secondary text-sm text-gray-700 font-medium">
                        {item.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-[340px] lg:flex-shrink-0 mt-8 lg:mt-0">
            <div className="my-6">
              <RoomAvailabilityCalendar
                roomId={room.id}
                price={price}
                onDateSelect={handleCalendarDateSelect}
                onDateRangeSelect={handleDateRangeSelect}
                onPriceUpdate={handlePriceUpdate}
                onBookNow={handleBookNow}
              />
            </div>

            {/* Hotel Rules */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg">
              <h3 className="font-primary text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">📋</span>
                Hotel Rules
              </h3>
              <div className="w-16 h-1 bg-blue-600 mb-4"></div>
              <p className="text-gray-600 font-secondary text-sm leading-relaxed mb-6">
                Please review our policies to ensure a pleasant stay at Hotel &
                Spa Adina.
              </p>
              <ul className="space-y-3">
                {hotelRules.map(({ rules }, idx) => (
                  <li key={idx} className="flex items-start gap-x-3 group">
                    <div className="bg-blue-100 rounded-full p-1 mt-0.5 group-hover:bg-blue-200 transition-colors duration-200">
                      <FaCheck className="text-blue-600 text-xs" />
                    </div>
                    <span className="font-secondary text-sm text-gray-700 leading-relaxed">
                      {rules}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center"
          onClick={handleLightboxClose}
        >
          <button
            onClick={handleLightboxClose}
            className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl transition-colors duration-200 z-10"
            aria-label="Close lightbox"
          >
            <FaTimes />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
            className="absolute left-4 md:left-8 text-white/50 hover:text-white text-3xl transition-all duration-200 hover:scale-110 z-10"
            aria-label="Previous image"
          >
            <FaChevronLeft />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
            className="absolute right-4 md:right-8 text-white/50 hover:text-white text-3xl transition-all duration-200 hover:scale-110 z-10"
            aria-label="Next image"
          >
            <FaChevronRight />
          </button>

          <div
            className="relative max-w-5xl max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={displayImages[selectedImage] || imageLg}
              alt={`${name} - Image ${selectedImage + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium">
              {selectedImage + 1} / {displayImages.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}